/** @prettier */
import React, { useState, useEffect, useCallback, Component } from "react";
import { FormControl, FormGroup, Button, ControlLabel } from "react-bootstrap";
import { getFileType } from "../util/imageUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    QuestionImageData as ImageData,
    RemovedImageData,
} from "./questionApi";

type ImageThumbnailProps = {
    image: ImageData;
};
const ImageThumbnail = ({ image }: ImageThumbnailProps) => {
    const [imgSrc, setImageSrc] = useState("");
    useEffect(() => {
        if (image.image instanceof File) {
            const imageReader = new FileReader();

            imageReader.onload = (e) =>
                // TODO: double check nuances here
                setImageSrc(e.target!.result as string);
            imageReader.readAsDataURL(image.image);
        } else {
            // cast because instanceof File doesn't narrow the union
            // would work better with a discriminator property
            const fileType = getFileType(image.fileName);
            setImageSrc("data:image/" + fileType + ";base64," + image.image);
        }
    }, [image]);

    return (
        <img className="question-editor-thumbnail" src={imgSrc} alt="Figure" />
    );
};

type ImageUploadProps = {
    addImage: (file: File) => void;
};
const ImageUpload = ({ addImage }: ImageUploadProps) => {
    const [showingSizeWarning, setShowingSizeWarning] = useState(false);
    const [uploadKey, setUploadKey] = useState(0);

    const onChange = useCallback(
        (event: React.FormEvent<FormControl>) => {
            const target = (event.currentTarget as any) as HTMLInputElement;
            const file = target.files?.[0];
            if (!file) return;
            // 1MB file size limit
            if (file.size > 1000000) {
                setShowingSizeWarning(true);
            } else {
                addImage(file);
                setUploadKey((i) => i + 1);
                setShowingSizeWarning(false);
            }
        },
        [addImage],
    );
    return (
        <div className="question-editor-image-element">
            <FormGroup
                className="question-editor-image-upload"
                controlId="question-editor-image-upload"
            >
                <FormControl
                    type="file"
                    key={uploadKey}
                    onChange={onChange}
                    accept=".png,.jpg,.jpeg"
                />
            </FormGroup>
            {showingSizeWarning ? (
                <span className="question-editor-image-size-warning">
                    Image must be smaller than 1MB
                </span>
            ) : (
                <></>
            )}
        </div>
    );
};

type ImageDisplayProps = {
    index: number;
    deleteImage: (index: number) => void;
    image: ImageData;
};
const ImageDisplay = ({ index, deleteImage, image }: ImageDisplayProps) => (
    <div className="question-editor-image-thumbnail question-editor-image-element">
        <ImageThumbnail image={image} />
        <Button
            bsClass="question-editor-delete"
            className="question-editor-image-delete fa-border"
            onClick={() => deleteImage(index)}
        >
            <span className="fa-layers fa-fw">
                <FontAwesomeIcon icon="times-circle" size="lg" />
                <FontAwesomeIcon
                    icon={["far", "times-circle"]}
                    className="question-editor-inverse-icon"
                    size="lg"
                />
            </span>
        </Button>
    </div>
);

export type ImageUploadGroupProps = {
    type: "stem" | "explanation";
    images: ImageData[];
    setImages: (images: ImageData[]) => void;
    removedImages: RemovedImageData[];
    setRemovedImages: (removedImages: RemovedImageData[]) => void;
};
class ImageUploadGroup extends Component<ImageUploadGroupProps> {
    constructor(props: ImageUploadGroupProps) {
        super(props);

        this.addImage = this.addImage.bind(this);
        this.deleteImage = this.deleteImage.bind(this);
    }
    deleteImage(index: number) {
        const imageData = this.props.images[index];

        // Remove from images list
        const imagesCopy = [...this.props.images];
        imagesCopy.splice(index, 1);
        this.props.setImages(imagesCopy);

        // If not a new image, add to removal list
        if (imageData.type === "new_image") {
            return;
        }
        const imageToDelete: RemovedImageData = {
            ...imageData,
            id: imageData.id,
            imageType: this.props.type === "stem" ? "STEM" : "EXPLANATION",
        };
        this.props.setRemovedImages(
            this.props.removedImages.concat(imageToDelete),
        );
    }
    addImage(newImage: File) {
        let imageData = {
            image: newImage,
            fileName: newImage.name,
            type: "new_image",
        } as const;

        this.props.setImages(this.props.images.concat(imageData));
    }
    render() {
        const label =
            this.props.type === "stem"
                ? "Question stem images"
                : "Explanation images";
        return (
            <FormGroup controlId="question-editor-image-upload">
                <ControlLabel>{label}</ControlLabel>
                <div className="question-editor-image-group">
                    {this.props.images.map((value, index) => {
                        return (
                            <ImageDisplay
                                key={index}
                                index={index}
                                image={value}
                                deleteImage={this.deleteImage}
                            />
                        );
                    })}
                    {this.props.images.length < 5 && (
                        <ImageUpload addImage={this.addImage} />
                    )}
                </div>
            </FormGroup>
        );
    }
}
export default ImageUploadGroup;
